import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { icons } from '../components/ui/Icons';
import Button from '../components/ui/Button';
import toast from 'react-hot-toast';

/**
 * Página: OwnerProfilePage (/pages/OwnerProfilePage.jsx)
 * Perfil público do dono de barbearia
 */
const OwnerProfilePage = () => {
  const { ownerId } = useParams();
  const navigate = useNavigate();
  const [owner, setOwner] = useState(null);
  const [barbershop, setBarbershop] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOwnerData = async () => {
      try {
        setLoading(true);

        // Buscar dados do dono
        const ownerDoc = await getDoc(doc(db, 'users', ownerId));
        
        if (!ownerDoc.exists()) {
          toast.error('Dono não encontrado');
          navigate('/');
          return;
        }

        const ownerData = { id: ownerDoc.id, ...ownerDoc.data() };
        setOwner(ownerData);

        // Buscar barbearia do dono (suporta formato antigo e novo)
        const barbershopId = ownerData.barbershopId || (ownerData.barbershops && ownerData.barbershops[0]);
        if (barbershopId) {
          const barbershopDoc = await getDoc(doc(db, 'barbershops', barbershopId));
          if (barbershopDoc.exists()) {
            setBarbershop({ id: barbershopDoc.id, ...barbershopDoc.data() });
          }
        }
      } catch (error) {
        console.error('Erro ao buscar dados do dono:', error);
        toast.error('Erro ao carregar perfil');
      } finally {
        setLoading(false);
      }
    };

    if (ownerId) {
      fetchOwnerData();
    }
  }, [ownerId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando perfil...</p>
        </div>
      </div>
    );
  }

  if (!owner) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Perfil não encontrado</p>
          <Button onClick={() => navigate('/')}>Voltar para Início</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header com foto e nome */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 h-32"></div>
          
          <div className="relative px-6 pb-6">
            {/* Foto de Perfil */}
            <div className="absolute -top-16 left-6">
              <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg overflow-hidden bg-gray-200">
                {owner.photoURL ? (
                  <img
                    src={owner.photoURL}
                    alt={owner.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-indigo-100">
                    {icons.user({ className: 'w-16 h-16 text-indigo-600' })}
                  </div>
                )}
              </div>
            </div>

            {/* Informações */}
            <div className="pt-20">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{owner.name}</h1>
                  <p className="text-gray-600 mt-1">Dono de Barbearia</p>
                  {owner.specialty && (
                    <span className="inline-block px-3 py-1 bg-indigo-100 text-indigo-700 text-sm rounded-full mt-2">
                      {owner.specialty}
                    </span>
                  )}
                </div>

                {barbershop && (
                  <Button
                    variant="primary"
                    onClick={() => navigate(`/barbershop/${barbershop.id}`)}
                  >
                    {icons.scissors({ className: 'w-5 h-5 mr-2' })}
                    Ver Barbearia
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Informações Profissionais */}
          {(owner.specialty || owner.experience) && (
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                {icons.briefcase && icons.briefcase({ className: 'w-6 h-6 mr-2 text-indigo-600' })}
                Informações Profissionais
              </h2>

              <div className="space-y-4">
                {owner.specialty && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Especialidade</p>
                    <p className="text-gray-900 font-semibold">{owner.specialty}</p>
                  </div>
                )}

                {owner.experience && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Experiência</p>
                    <p className="text-gray-900 font-semibold">{owner.experience}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Informações de Contato */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              {icons.phone({ className: 'w-6 h-6 mr-2 text-indigo-600' })}
              Informações de Contato
            </h2>

            <div className="space-y-4">
            {owner.email && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  {icons.mail({ className: 'w-5 h-5 text-indigo-600' })}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email do Proprietário</p>
                  <a
                    href={`mailto:${owner.email}`}
                    className="text-gray-900 hover:text-indigo-600 transition-colors"
                  >
                    {owner.email}
                  </a>
                </div>
              </div>
            )}

            {owner.phone && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  {icons.phone({ className: 'w-5 h-5 text-green-600' })}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Telefone do Proprietário</p>
                  <a
                    href={`tel:${owner.phone}`}
                    className="text-gray-900 hover:text-green-600 transition-colors"
                  >
                    {owner.phone}
                  </a>
                </div>
              </div>
            )}

            {barbershop && (
              <div className="flex items-start gap-3 pt-4 border-t">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  {icons.briefcase({ className: 'w-5 h-5 text-purple-600' })}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Barbearia</p>
                  <button
                    onClick={() => navigate(`/barbershop/${barbershop.id}`)}
                    className="text-gray-900 hover:text-indigo-600 transition-colors font-semibold flex items-center gap-1"
                  >
                    {barbershop.name}
                    {icons.externalLink({ className: 'w-4 h-4' })}
                  </button>
                  {barbershop.city && (
                    <p className="text-sm text-gray-500 mt-1">{barbershop.city}</p>
                  )}
                </div>
              </div>
            )}
            </div>
          </div>
        </div>

        {/* Botão Voltar */}
        <div className="mt-8 text-center">
          <Button variant="secondary" onClick={() => window.history.back()}>
            {icons.chevronLeft({ className: 'w-5 h-5 mr-2' })}
            Voltar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OwnerProfilePage;
